import React from 'react';
import { isEqual } from 'lodash';
import { List, ListItemIcon, ListItemButton, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import { selectFileSource, selectAllFileSources, selectCurrentDepth } from '@/redux/selectors/fileManagerSelectors';
import { CLEAN_PATH_REGEX, clearForwardLocations, getFolder, setFileSource } from '@/redux/slices/fileManagerSlice';
import { Connector, FileConnector, FileSource } from '@/redux/slices/slice_types';
import FolderIcon from '../FileItem/FolderIcon';
import FileItemText from '../FileItem/FileItemText';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

const FileSourcesColumn: React.FC = () => {
  const currentDepth = useAppSelector(selectCurrentDepth);
  const currentBasePath = useAppSelector(selectFileSource);
  const allFileSources = useAppSelector(selectAllFileSources);
  const dispatch = useAppDispatch();

  const handleClick = async (item: { slug: string }) => {
    const cleanItemName = item.slug.replace(CLEAN_PATH_REGEX, '');
    const isCurrentBase = isEqual(currentBasePath, cleanItemName);

    if (!isCurrentBase || (currentDepth !== 0 && isCurrentBase)) {
      dispatch(setFileSource(cleanItemName));
      dispatch(clearForwardLocations());
      dispatch(getFolder({ path: [cleanItemName] }));
    }
  };

  return (
    <StyledList component="nav" aria-label="base folders">
      {allFileSources.map((fileSource: FileSource) => {
        const basePath = fileSource.slug;
        const selected = currentBasePath === basePath;
        const noConnectors =
          fileSource?.is_read_only || (fileSource.connectors as FileConnector[])?.every((value: FileConnector) => value?.allows_uploads === false);

        return (
          <ListItemButton
            className="source-item"
            key={fileSource.slug}
            selected={selected}
            onClick={() => handleClick(fileSource)}
            sx={{ borderRadius: 0 }}
          >
            <StyledListItemIcon>
              <FolderIcon open={selected} />
            </StyledListItemIcon>
            <FileItemText name={fileSource.name} />
            {noConnectors && (
              <Tooltip title="Read-only">
                <StyledIconButton icon="read_only" height={16} />
              </Tooltip>
            )}
          </ListItemButton>
        );
      })}
    </StyledList>
  );
};

export default FileSourcesColumn;

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: 1rem;
  opacity: 0.6;
  margin-right: ${({ theme }) => theme.spacing(1.5)};
  &:hover {
    cursor: pointer;
  }
`;

const StyledList = styled(List)<{ component: string }>`
  border-top-left-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-bottom-left-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const StyledIconButton = styled(PolarisIcon)`
  margin-left: ${({ theme }) => theme.spacing(1)};
  opacity: 0.4;
`;
